import axios from 'axios';
import React, { useContext, useEffect, useReducer } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import LoadingBox from '../../component/LoadingBox';
import MessageBox from '../../component/MessageBox';
import { Store } from '../../Store';
import { getError } from '../../utils';
import { api } from '../../Api';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true, error: '' };
    case 'FETCH_SUCCESS':
      return { ...state, summery: action.payload, loading: false };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
};

// Safely pull counts from dashboard summary buckets,
// which come back as either an empty array or a single-row array.
function pick(bucket) {
  const row = Array.isArray(bucket) ? bucket[0] : null;
  return {
    count: Number(row?.numOrders) || 0,
    quantity: Number(row?.totalQuantity) || 0,
  };
}

const fmt = (n) => Number(n || 0).toLocaleString();

export default function AgentDashboardScreen() {
  const [{ loading, summery, error }, dispatch] = useReducer(reducer, {
    loading: true,
    error: '',
    summery: null,
  });
  const { state } = useContext(Store);
  const { userInfo } = state;
  // The topup queue is its own service, so it is its own small fetch rather
  // than something bolted onto the orders summary. The rows are the oldest
  // requests still waiting — the ones Accounts should be doing first. Only
  // staff holding the topup (Accounts) permission get this section at all.
  const canTopups = Boolean(userInfo?.permissions?.canApproveTopups);
  const [topups, setTopups] = React.useState({
    pending: 0,
    verified: 0,
    pendingAmount: 0,
    approvedAmount: 0,
    rows: [],
  });

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        dispatch({ type: 'FETCH_REQUEST' });
        const { data } = await axios.get(
          `${api}/api/availablebalance/agentdashboard`,
          { headers: { Authorization: `Bearer ${userInfo.token}` } }
        );
        if (!cancelled) dispatch({ type: 'FETCH_SUCCESS', payload: data });
      } catch (err) {
        if (!cancelled) dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
      }
    };
    const fetchTopups = async () => {
      try {
        const { data } = await axios.get(
          `${api}/api/topup/queue?status=pending&pageSize=5&scope=accounts`,
          { headers: { Authorization: `Bearer ${userInfo.token}` } }
        );
        if (!cancelled) {
          setTopups({
            pending: Number(data?.totals?.pending) || 0,
            verified: Number(data?.totals?.verified) || 0,
            pendingAmount: Number(data?.totals?.byStatus?.pending?.amount) || 0,
            approvedAmount: Number(data?.totals?.approvedAmount) || 0,
            rows: Array.isArray(data?.rows) ? data.rows : [],
          });
        }
      } catch (err) {
        /* the queue card is a shortcut, not the dashboard — stay quiet */
      }
    };
    if (userInfo?.token) {
      fetchData();
      if (canTopups) fetchTopups();
    }
    return () => { cancelled = true; };
  }, [userInfo]);

  const orderPending = pick(summery?.unPaidOrders);
  const orderApproved = pick(summery?.paidOrders);
  const wdPending = pick(summery?.unPaidWithdraw);
  const wdApproved = pick(summery?.paidWithdraw);

  const orderTotal = orderPending.count + orderApproved.count;
  const wdTotal = wdPending.count + wdApproved.count;

  return (
    <div style={{ width: '87%', float: 'right' }}>
      <Helmet>
        <title>Agent Dashboard | ClickCart</title>
      </Helmet>

      <Container fluid="lg" className="mb-4 pt-3">
        <div className="mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <h4 className="mb-1 fw-semibold">Agent Dashboard</h4>
            <div className="text-muted small">
              Welcome back{userInfo?.name ? `, ${userInfo.name}` : ''}. Here's today's snapshot.
            </div>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <Button as={Link} to="/agent/orders" size="sm" variant="outline-dark">
              Manage Orders
            </Button>
            <Button as={Link} to="/agent/withdraw" size="sm" variant="outline-dark">
              Manage Withdraws
            </Button>
            {canTopups ? (
              <Button
                as={Link}
                to="/agent/topup"
                size="sm"
                variant={topups.pending ? 'danger' : 'outline-dark'}
              >
                Topup Requests{topups.pending ? ` (${fmt(topups.pending)})` : ''}
              </Button>
            ) : null}
            {/* <Button as={Link} to="/agent/pointlog" size="sm" variant="outline-dark">
              Point Transfers
            </Button> */}
          </div>
        </div>

        {loading ? (
          <div className="p-4 text-center"><LoadingBox /></div>
        ) : error ? (
          <MessageBox variant="danger">{error}</MessageBox>
        ) : !summery ? (
          <MessageBox variant="warning">No summary data available.</MessageBox>
        ) : (
          <>
            {/* Topups — Accounts' most time-sensitive job, so it leads. The
                queue moves during the day, so the oldest few are listed here
                rather than only counted. */}
            {canTopups ? (
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="text-uppercase text-muted small fw-semibold" style={{ letterSpacing: '0.06em' }}>
                  Topups
                </div>
                <Link to="/agent/topup" className="small text-decoration-none">
                  Open the queue &rarr;
                </Link>
              </div>
              <Row className="g-2">
                <Col xs={12} md={4}>
                  <Card className={`border-0 shadow-sm h-100 ${topups.pending ? 'border-start border-4 border-danger' : ''}`}>
                    <Card.Body className="py-3">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="text-muted text-uppercase" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                          Waiting for you
                        </div>
                        <Badge bg={topups.pending ? 'danger' : 'secondary'}>{fmt(topups.pending)}</Badge>
                      </div>
                      <div className="h4 mb-0 fw-semibold text-danger mt-2">
                        {fmt(topups.pending)} <span className="small text-muted fw-normal">to verify</span>
                      </div>
                      <div className="small text-muted">BDT {fmt(topups.pendingAmount)} to check</div>
                      <Link to="/agent/topup?status=pending" className="small text-decoration-none">
                        Check payments &rarr;
                      </Link>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={12} md={4}>
                  <Card className="border-0 shadow-sm h-100">
                    <Card.Body className="py-3">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="text-muted text-uppercase" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                          Verified, with admin
                        </div>
                        <Badge bg="primary">{fmt(topups.verified)}</Badge>
                      </div>
                      <div className="h4 mb-0 fw-semibold text-primary mt-2">
                        {fmt(topups.verified)} <span className="small text-muted fw-normal">verified</span>
                      </div>
                      <div className="small text-muted">You have done your part on these</div>
                      <Link to="/agent/topup?status=verified" className="small text-decoration-none">
                        See them &rarr;
                      </Link>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={12} md={4}>
                  <Card className="border-0 shadow-sm h-100">
                    <Card.Body className="py-3">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="text-muted text-uppercase" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                          Approved so far
                        </div>
                        <Badge bg="success">All time</Badge>
                      </div>
                      <div className="h4 mb-0 fw-semibold text-success mt-2">
                        BDT {fmt(topups.approvedAmount)}
                      </div>
                      <div className="small text-muted">Released into shopping balances</div>
                      <Link to="/agent/topup?status=approved" className="small text-decoration-none">
                        View approved &rarr;
                      </Link>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {topups.rows.length ? (
                <Card className="border-0 shadow-sm mt-2">
                  <Card.Body className="py-2">
                    <div className="text-muted text-uppercase mb-1" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                      Oldest waiting
                    </div>
                    {topups.rows.map((row) => (
                      <Link
                        key={row._id}
                        to="/agent/topup?status=pending"
                        className="d-flex align-items-center gap-2 py-2 border-bottom text-decoration-none text-body"
                      >
                        <div className="fw-semibold" style={{ minWidth: 110 }}>
                          BDT {fmt(row.amountBdt)}
                        </div>
                        <div className="flex-grow-1 text-truncate small">
                          {row.name} <span className="text-muted">@{row.username}</span>
                          <span className="text-muted text-capitalize">
                            {' '}&middot; {String(row.method || '').replace('_', ' ')}
                          </span>
                        </div>
                        <div className="small text-muted d-none d-sm-block">
                          {new Date(row.createdAt).toLocaleString()}
                        </div>
                        <Badge bg="warning" text="dark">verify</Badge>
                      </Link>
                    ))}
                  </Card.Body>
                </Card>
              ) : null}
            </div>
            ) : null}

            {/* Orders section */}
            <div className="mb-3">
              <div className="text-uppercase text-muted small fw-semibold mb-2" style={{ letterSpacing: '0.06em' }}>
                Orders
              </div>
              <Row className="g-2">
                <Col xs={12} md={4}>
                  <Card className="border-0 shadow-sm h-100">
                    <Card.Body className="py-3">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="text-muted text-uppercase" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                          Pending Orders
                        </div>
                        <Badge bg="danger">{fmt(orderPending.count)}</Badge>
                      </div>
                      <div className="h4 mb-0 fw-semibold text-danger mt-2">
                        {fmt(orderPending.quantity)} <span className="small text-muted fw-normal">items</span>
                      </div>
                      <Link to="/agent/orders?type=pending" className="small text-decoration-none">
                        Review pending →
                      </Link>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={12} md={4}>
                  <Card className="border-0 shadow-sm h-100">
                    <Card.Body className="py-3">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="text-muted text-uppercase" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                          Approved Orders
                        </div>
                        <Badge bg="success">{fmt(orderApproved.count)}</Badge>
                      </div>
                      <div className="h4 mb-0 fw-semibold text-success mt-2">
                        {fmt(orderApproved.quantity)} <span className="small text-muted fw-normal">items</span>
                      </div>
                      <Link to="/agent/orders?type=approved" className="small text-decoration-none">
                        View approved →
                      </Link>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={12} md={4}>
                  <Card className="border-0 shadow-sm h-100">
                    <Card.Body className="py-3">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="text-muted text-uppercase" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                          Total Orders
                        </div>
                        <Badge bg="dark">{fmt(orderTotal)}</Badge>
                      </div>
                      <div className="h4 mb-0 fw-semibold text-dark mt-2">
                        {fmt(orderPending.quantity + orderApproved.quantity)} <span className="small text-muted fw-normal">items</span>
                      </div>
                      <Link to="/agent/orders?type=all" className="small text-decoration-none">
                        See all →
                      </Link>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>

            {/* Withdrawals section */}
            <div className="mb-3">
              <div className="text-uppercase text-muted small fw-semibold mb-2" style={{ letterSpacing: '0.06em' }}>
                Withdrawals
              </div>
              <Row className="g-2">
                <Col xs={12} md={4}>
                  <Card className="border-0 shadow-sm h-100">
                    <Card.Body className="py-3">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="text-muted text-uppercase" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                          Pending Withdraws
                        </div>
                        <Badge bg="danger">{fmt(wdPending.count)}</Badge>
                      </div>
                      <div className="h4 mb-0 fw-semibold text-danger mt-2">
                        {fmt(wdPending.count)} <span className="small text-muted fw-normal">requests</span>
                      </div>
                      <Link to="/agent/withdraw" className="small text-decoration-none">
                        Review pending →
                      </Link>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={12} md={4}>
                  <Card className="border-0 shadow-sm h-100">
                    <Card.Body className="py-3">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="text-muted text-uppercase" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                          Approved Withdraws
                        </div>
                        <Badge bg="success">{fmt(wdApproved.count)}</Badge>
                      </div>
                      <div className="h4 mb-0 fw-semibold text-success mt-2">
                        {fmt(wdApproved.count)} <span className="small text-muted fw-normal">requests</span>
                      </div>
                      <Link to="/agent/withdraw" className="small text-decoration-none">
                        View approved →
                      </Link>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={12} md={4}>
                  <Card className="border-0 shadow-sm h-100">
                    <Card.Body className="py-3">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="text-muted text-uppercase" style={{ letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                          Total Withdraws
                        </div>
                        <Badge bg="dark">{fmt(wdTotal)}</Badge>
                      </div>
                      <div className="h4 mb-0 fw-semibold text-dark mt-2">
                        {fmt(wdTotal)} <span className="small text-muted fw-normal">requests</span>
                      </div>
                      <Link to="/agent/withdraw" className="small text-decoration-none">
                        See all →
                      </Link>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>

          </>
        )}
      </Container>
    </div>
  );
}
